// This code initializes a new QueryClient that is used in all of the components in our app.
// It also sets up some error handling for mutations and queries.
// The error handling will send the error to Sentry, and also show an alert to the user.

import { captureException, withScope } from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import DangerouslySetHtmlContent from "dangerously-set-html-content";
import { AxiosError } from "axios";

const onErrorFunction = async (error: AxiosError<any>) => {
  let text: string | undefined;
  if (error.request.responseType === "blob") {
    text = await error?.response?.data?.text();
  } else {
    text = error?.response?.data;
  }
  if (text) {
    enqueueSnackbar(
      <div>{text ? <DangerouslySetHtmlContent html={text} /> : ""}</div>,
      { variant: "error" },
    );
  }
};

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      if (err?.response?.status >= 500) {
        withScope((scope) => {
          scope.setContext("mutation", {
            mutationId: mutation.mutationId,
            variables: mutation.state.variables,
          });
          if (mutation.options.mutationKey) {
            scope.setFingerprint(
              // Duplicate to prevent modification
              Array.from(mutation.options.mutationKey) as string[],
            );
          }

          captureException(err);
        });
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (err?.response?.status >= 500) {
        withScope((scope) => {
          scope.setContext("query", { queryHash: query.queryHash });
          scope.setFingerprint([query.queryHash.replaceAll(/[0-9]/g, "0")]);
        });
        captureException(err);
      }
    },
  }),
  defaultOptions: {
    queries: {
      onError: async (error: any) => {
        await onErrorFunction(error);
      },
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
    mutations: {
      onError: async (error: any) => {
        await onErrorFunction(error);
      },
    },
  },
});

export { queryClient };
